import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
} from '@mui/material';
import {
  useLocation,
} from 'react-router-dom';
import {
  captureSessionTokenFromHash,
  getCourseActivityLaunchToken,
  getSessionAuthorizationHeaders,
} from '../services/sessionAuth';
import {
  replaceWithSession,
  replaceWithSessionAndCourseActivity,
} from '../services/sessionNavigation';
import './retzia/RetziaShell.css';

interface SessionUser {
  id: number;
  moodle_issuer: string;
  moodle_user_id: number;
  username: string;
  display_name: string;
}

interface SessionResponse {
  ok: boolean;
  authenticated?: boolean;
  error?: string;
  user?: SessionUser;
}

type ScreenState =
  | 'checking'
  | 'unauthenticated'
  | 'error';

function Login() {
  const location = useLocation();

  const loginParameters =
    new URLSearchParams(location.search);

  const requestedLanguage =
    (
      location.state?.language ??
      loginParameters.get('language')
    ) as string | undefined;

  const requestedReturnTo =
    (
      location.state?.returnTo ??
      loginParameters.get('returnTo')
    ) as string | undefined;

  const [screenState, setScreenState] =
    useState<ScreenState>('checking');

  useEffect(() => {
    let cancelled = false;

    const authenticateFromSession = async () => {
      try {
        captureSessionTokenFromHash();

        const authorizationHeaders =
          getSessionAuthorizationHeaders();

        const response = await fetch(
          '/api/session.php',
          {
            method: 'GET',
            credentials: 'include',
            cache: 'no-store',
            headers: {
              ...authorizationHeaders,
              Accept: 'application/json',
            },
          }
        );

        const rawResponse =
          await response.text();

        let data: SessionResponse;

        try {
          data = JSON.parse(
            rawResponse
          ) as SessionResponse;
        } catch {
          if (!cancelled) {
            setScreenState('error');
          }

          return;
        }

        if (cancelled) {
          return;
        }

        if (
          !response.ok ||
          !data.ok ||
          !data.authenticated ||
          !data.user
        ) {
          setScreenState('unauthenticated');
          return;
        }

        const courseActivityLaunch =
          getCourseActivityLaunchToken();

        if (courseActivityLaunch) {
          replaceWithSessionAndCourseActivity(
            '/course-activity',
            courseActivityLaunch
          );
          return;
        }

        switch (requestedLanguage) {
          case 'english':
          case 'german':
          case 'french': {
            const allowedDestinations = [
              'language',
              'chat',
              'grammar',
              'taskreviewer',
            ];

            const destination =
              requestedReturnTo &&
              allowedDestinations.includes(
                requestedReturnTo
              )
                ? requestedReturnTo
                : 'language';

            replaceWithSession(
              `/${destination}?language=${requestedLanguage}`
            );
            return;
          }

          case 'english_taskreviewer':
            replaceWithSession(
              '/taskreviewer?language=english'
            );
            return;

          case 'german_taskreviewer':
            replaceWithSession(
              '/taskreviewer?language=german'
            );
            return;

          case 'french_taskreviewer':
            replaceWithSession(
              '/taskreviewer?language=french'
            );
            return;

          default:
            replaceWithSession('/home');
        }
      } catch (error) {
        console.error(
          'Unable to verify the RETZIA session.',
          error
        );

        if (!cancelled) {
          setScreenState('error');
        }
      }
    };

    authenticateFromSession();

    return () => {
      cancelled = true;
    };
  }, [
    requestedLanguage,
    requestedReturnTo,
  ]);

  const getRetzBaseUrl = (): string =>
    window.location.hostname === 'devretzia.retz.mx'
      ? 'https://staging.retz.mx'
      : 'https://retz.mx';

  const continueWithRetz = () => {
    window.open(
      `${getRetzBaseUrl()}/local/retzia/launch.php`,
      '_top'
    );
  };

  const goToRetz = () => {
    window.open(`${getRetzBaseUrl()}/`, '_top');
  };

  return (
    <Box
      sx={{
        minHeight: '100dvh',
        display: 'grid',
        placeItems: 'center',
        padding: 3,
        overflow: 'hidden',
        background:
          'radial-gradient(circle at 12% 18%, rgba(221,242,142,.28), transparent 28%), radial-gradient(circle at 88% 82%, rgba(107,99,255,.20), transparent 34%), linear-gradient(145deg, #fbfbff 0%, #f4f2ff 58%, #fff 100%)',
      }}
    >
      <Box
        sx={{
          width: 'min(460px, 100%)',
          padding: {
            xs: 3,
            sm: 5,
          },
          border: '1px solid rgba(81,77,216,.12)',
          borderRadius: '32px',
          background: 'rgba(255,255,255,.94)',
          boxShadow:
            '0 28px 80px rgba(38,31,115,.14)',
          textAlign: 'center',
          backdropFilter: 'blur(16px)',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          <span
            className="retzia-home__symbol"
            aria-hidden="true"
          >
            <span className="retzia-home__symbol-mark" />
            <i className="retzia-home__spark" />
          </span>
        </Box>

        <Box
          sx={{
            marginTop: 2,
            color: '#514dd8',
            fontSize: '27px',
            fontWeight: 900,
            letterSpacing: '-0.03em',
          }}
        >
          RETZIA
        </Box>

        <Box
          sx={{
            marginTop: 0.25,
            color: '#74798c',
            fontSize: '14px',
            fontWeight: 600,
          }}
        >
          Práctica y apoyo para tu curso
        </Box>

        {screenState === 'checking' ? (
          <>
            <CircularProgress
              size={34}
              sx={{
                marginTop: 4,
                color: '#514dd8',
              }}
            />

            <Box
              sx={{
                marginTop: 2,
                color: '#5f6474',
                fontSize: '15px',
              }}
            >
              Conectando con tu cuenta de RETZ…
            </Box>
          </>
        ) : (
          <>
            <Box
              sx={{
                marginTop: 3.5,
                color: '#101828',
                fontSize: {
                  xs: '25px',
                  sm: '28px',
                },
                fontWeight: 900,
                letterSpacing: '-0.025em',
              }}
            >
              Inicia sesión
            </Box>

            <Box
              sx={{
                marginTop: 1.25,
                color: '#687187',
                fontSize: '15px',
                lineHeight: 1.65,
              }}
            >
              Continúa con la misma cuenta que usas
              para entrar a RETZ.
            </Box>

            <Button
              onClick={continueWithRetz}
              variant="contained"
              fullWidth
              sx={{
                marginTop: 3,
                minHeight: '52px',
                padding: '12px 22px',
                borderRadius: '16px',
                background:
                  'linear-gradient(135deg, #6b63ff, #514dd8)',
                boxShadow:
                  '0 14px 30px rgba(81,77,216,.25)',
                fontSize: '15px',
                fontWeight: 800,
                textTransform: 'none',
                '&:hover': {
                  background:
                    'linear-gradient(135deg, #5c55ed, #403bc1)',
                  boxShadow:
                    '0 16px 34px rgba(81,77,216,.30)',
                },
              }}
            >
              Continuar con RETZ
              <Box
                component="span"
                aria-hidden="true"
                sx={{marginLeft: 1}}
              >
                →
              </Box>
            </Button>

            <Box
              sx={{
                marginTop: 1.5,
                color: '#85899a',
                fontSize: '12.5px',
                lineHeight: 1.5,
              }}
            >
              Tu contraseña se ingresa directamente
              en RETZ.
            </Box>

            <Button
              onClick={goToRetz}
              variant="text"
              sx={{
                marginTop: 1.5,
                color: '#514dd8',
                fontSize: '14px',
                fontWeight: 750,
                textTransform: 'none',
                '&:hover': {
                  background: 'rgba(81,77,216,.06)',
                },
              }}
            >
              Volver al inicio de RETZ
            </Button>

            {screenState === 'error' && (
              <Box
                sx={{
                  marginTop: 1.5,
                  color: '#9b3347',
                  fontSize: '13px',
                }}
              >
                Ocurrió un problema al comprobar la
                sesión. Puedes continuar desde RETZ.
              </Box>
            )}
          </>
        )}
      </Box>
    </Box>
  );
}

export default Login;
